// State Machine Configuration - Simplified Application State Machine
// ✅ NO MAGIC STRINGS: Use enums/constants for all state and event names

import debugLogger from "../utils/debugLogger";

import {
  AppState,
  StateEvent,
  StateTransition,
  StateMachineConfig,
  AppPhase,
} from "./types";

/**
 * Load state machine configuration from YAML file
 * Falls back to default config if YAML file is not found
 *
 * NOTE: This function is synchronous and returns default config.
 * The actual YAML config is loaded asynchronously in useStateMachine hook.
 */
export const createStateMachineConfig = (): StateMachineConfig => {
  // Always return default config synchronously
  // The async YAML loading happens in useStateMachine hook
  return createDefaultStateMachineConfig();
};

/**
 * Default State Machine Configuration (fallback)
 *
 * Flow: INITIALIZATION -> RECOVERY -> AUTHENTICATION/ONBOARDING -> WORKSPACE
 * Error handling: Any phase can transition to ERROR, ERROR can recover to RECOVERY
 */
const createDefaultStateMachineConfig = (): StateMachineConfig => {
  const transitions: StateTransition[] = [
    // Initialization phase
    {
      from: AppState.INITIALIZATION,
      event: StateEvent.INITIALIZATION_COMPLETE,
      to: AppState.RECOVERY,
    },
    {
      from: AppState.INITIALIZATION,
      event: StateEvent.INITIALIZATION_FAILED,
      to: AppState.ERROR,
    },

    // Recovery phase - checks authentication status
    {
      from: AppState.RECOVERY,
      event: StateEvent.AUTH_DATA_MISSING,
      to: AppState.AUTHENTICATION,
    },
    {
      from: AppState.RECOVERY,
      event: StateEvent.AUTH_DATA_PRESENT,
      to: AppState.ONBOARDING,
    },
    {
      from: AppState.RECOVERY,
      event: StateEvent.ERROR_OCCURRED,
      to: AppState.ERROR,
    },

    // Authentication phase
    {
      from: AppState.AUTHENTICATION,
      event: StateEvent.LOGIN_SUCCESS,
      to: AppState.ONBOARDING,
    },
    {
      from: AppState.AUTHENTICATION,
      event: StateEvent.LOGIN_FAILED,
      to: AppState.ERROR,
    },
    {
      from: AppState.AUTHENTICATION,
      event: StateEvent.ERROR_OCCURRED,
      to: AppState.ERROR,
    },

    // Onboarding phase
    {
      from: AppState.ONBOARDING,
      event: StateEvent.ONBOARDING_COMPLETE,
      to: AppState.WORKSPACE,
    },
    {
      from: AppState.ONBOARDING,
      event: StateEvent.ORG_SELECTED,
      to: AppState.WORKSPACE,
    },
    {
      from: AppState.ONBOARDING,
      event: StateEvent.ONBOARDING_FAILED,
      to: AppState.ERROR,
    },
    {
      from: AppState.ONBOARDING,
      event: StateEvent.LOGOUT,
      to: AppState.AUTHENTICATION,
    },
    {
      from: AppState.ONBOARDING,
      event: StateEvent.ERROR_OCCURRED,
      to: AppState.ERROR,
    },

    // Workspace phase
    {
      from: AppState.WORKSPACE,
      event: StateEvent.LOGOUT,
      to: AppState.AUTHENTICATION,
    },
    {
      from: AppState.WORKSPACE,
      event: StateEvent.ORG_SWITCHED,
      to: AppState.WORKSPACE,
    },
    {
      from: AppState.WORKSPACE,
      event: StateEvent.ERROR_OCCURRED,
      to: AppState.ERROR,
    },
    // Removed APP_EXIT transition to maintain INITIALIZATION as only entry point

    // Error phase - can recover to recovery
    {
      from: AppState.ERROR,
      event: StateEvent.RECOVER_FROM_ERROR,
      to: AppState.RECOVERY,
    },
    // Removed APP_EXIT transition to maintain INITIALIZATION as only entry point
  ];

  const phases: AppPhase[] = [
    {
      state: AppState.INITIALIZATION,
      dataRequirements: {
        requiredModules: [
          "core",
          "auth",
          "organization",
          "project",
          "application",
        ],
        requiredData: ["appConfig", "featureFlags"],
        optionalData: ["userPreferences"],
      },
      allowedRoutes: {
        routes: ["/loading", "/error"],
        redirectTo: "/loading",
      },
      description:
        "Application initialization - loading core modules and configuration",
    },
    {
      state: AppState.RECOVERY,
      dataRequirements: {
        requiredModules: ["auth"],
        requiredData: ["authToken", "userSession"],
        optionalData: ["userProfile"],
      },
      allowedRoutes: {
        routes: ["/recovery", "/loading", "/error"],
        redirectTo: "/recovery",
      },
      description:
        "Recovery phase - checking authentication status and session validity",
    },
    {
      state: AppState.AUTHENTICATION,
      dataRequirements: {
        requiredModules: ["auth"],
        requiredData: [],
        optionalData: ["authProviders", "loginMethods"],
      },
      allowedRoutes: {
        routes: ["/auth/login", "/auth/register", "/auth/callback", "/error"],
        redirectTo: "/auth/login",
      },
      description: "Authentication phase - user login and authentication",
    },
    {
      state: AppState.ONBOARDING,
      dataRequirements: {
        requiredModules: ["auth", "organization"],
        requiredData: ["userProfile", "userOrganizations"],
        optionalData: ["onboardingProgress", "userPreferences"],
      },
      allowedRoutes: {
        routes: [
          "/onboarding",
          "/organizations/create",
          "/organizations/select",
          "/error",
        ],
        redirectTo: "/onboarding",
      },
      description: "Onboarding phase - user setup and organization selection",
    },
    {
      state: AppState.WORKSPACE,
      dataRequirements: {
        requiredModules: ["auth", "organization", "project", "application"],
        requiredData: [
          "userProfile",
          "selectedOrganization",
          "userPermissions",
        ],
        optionalData: ["recentProjects", "userPreferences", "notifications"],
      },
      allowedRoutes: {
        routes: [
          "/dashboard",
          "/organizations/*",
          "/projects/*",
          "/applications/*",
          "/user/*",
          "/settings",
          "/error",
        ],
        redirectTo: "/dashboard",
      },
      description: "Workspace phase - main application functionality",
    },
    {
      state: AppState.ERROR,
      dataRequirements: {
        requiredModules: ["core"],
        requiredData: ["errorInfo"],
        optionalData: ["errorContext", "recoveryOptions"],
      },
      allowedRoutes: {
        routes: ["/error", "/recovery"],
        redirectTo: "/error",
      },
      description: "Error phase - handling application errors and recovery",
    },
  ];

  return {
    initialState: AppState.INITIALIZATION,
    transitions,
    phases,
    onTransition: (from: AppState, to: AppState, event: StateEvent) => {
      debugLogger.debug(`State transition: ${from} -> ${to} (event: ${event})`);
    },
  };
};

/**
 * Get phase information for a specific state
 */
export const getPhaseForState = (state: AppState): AppPhase | null => {
  const config = createStateMachineConfig();
  return config.phases.find((phase) => phase.state === state) || null;
};

/**
 * Get allowed routes for a specific state
 */
export const getAllowedRoutesForState = (state: AppState): string[] => {
  const phase = getPhaseForState(state);
  return phase?.allowedRoutes.routes || [];
};

/**
 * Get data requirements for a specific state
 */
export const getDataRequirementsForState = (state: AppState) => {
  const phase = getPhaseForState(state);
  return phase?.dataRequirements || null;
};
